import { useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import courseActions from '@redux/course/actions';
import { RootState } from '@redux/reducer';

const { fetchCourseDetailAction, fetchCourseDetailSeoAction } = courseActions;

const useCourseDetail = () => {
  const dispatch = useDispatch();
  const { courseDetailSeoState, courseDetailState, courseEnrollmentState } = useSelector((state: RootState) => ({
    courseDetailSeoState: state.Course.get('courseDetailSeoState'),
    courseDetailState: state.Course.get('courseDetailState'),
    courseEnrollmentState: state.Course.get('courseEnrollmentState'),
  }));

  const handleFetchCourseDetail = (courseIdOrIdName: string) => {
    dispatch(fetchCourseDetailAction.request({ courseIdOrIdName }));
  };

  const handleResetCourseDetail = () => {
    dispatch(fetchCourseDetailAction.reset());
    dispatch(fetchCourseDetailSeoAction.reset());
  };

  const { data: courseDetailSeoData, isLoading: isCourseDetailSeoLoading } = courseDetailSeoState;
  const { data: courseDetailData, isLoading: isCourseDetailLoading, isError: isCourseDetailError } = courseDetailState;
  const { data: courseEnrollmentData } = courseEnrollmentState;

  const {
    id,
    idName,
    name,
    productLearningTypeId,
    description,
    seoDescription,
    commercialMessage,
    seoEnabled,
    instructors,
    gtmContentId,
    highlightImage2xUrl,
    highlightImage1xUrl,
    hasCertificate,
    hasCertificateBundle,
    criteriaCertificate,
    price,
    videoCount,
    totalVideoDuration,
    countdownDay,
    attachmentFileCount,
    quizCount,
    assignmentCount,
    examinationCount,
    articleCount,
    review,
    isUseMailTemplate,
    mailContent,
    mailSubject,
    tweeterContent,
    canDiscuss,
    useVideoPreviewCourseBanner,
    videoPreviewUrl,
    videoPreviewSubtitles,
    enrollmentStatus,
    university,
    universityLogoUrl,
    degrees,
    examExpiredAt,
    bannerPrimaryColor,
    bannerSecondaryColor,
    degreeCourseGroups,
  } = useMemo(() => {
    return {
      id: courseDetailSeoData?.id,
      idName: courseDetailSeoData?.idName,
      name: courseDetailSeoData?.name,
      productLearningTypeId: courseDetailSeoData?.productLearningTypeId,
      gtmContentId: courseDetailData?.gtmContentId,
      description: courseDetailSeoData?.description,
      seoDescription: courseDetailSeoData?.seoDescription,
      commercialMessage: courseDetailSeoData?.commercialMessage,
      seoEnabled: courseDetailSeoData?.seoEnable,
      instructors: courseDetailSeoData?.instructors,
      highlightImage2xUrl: courseDetailData?.highlightImage2xUrl,
      highlightImage1xUrl: courseDetailData?.highlightImage1xUrl,
      hasCertificate: courseDetailData?.hasCertificate,
      hasCertificateBundle: courseDetailData?.hasCertificateBundle,
      criteriaCertificate: courseDetailData?.criteriaCertificate,
      price: courseDetailData?.price,
      videoCount: courseDetailData?.videoCount,
      totalVideoDuration: courseDetailData?.totalVideoDuration,
      countdownDay: courseDetailData?.countdownDay,
      attachmentFileCount: courseDetailData?.attachmentFileCount,
      quizCount: courseDetailData?.quizCount,
      assignmentCount: courseDetailData?.assignmentCount,
      examinationCount: courseDetailData?.examinationCount,
      articleCount: courseDetailData?.articleCount,
      review: courseDetailData?.review,
      isUseMailTemplate: courseDetailData?.isUseMailTemplate,
      mailContent: courseDetailData?.mailContent,
      mailSubject: courseDetailData?.mailSubject,
      tweeterContent: courseDetailData?.tweeterContent,
      canDiscuss: courseEnrollmentData?.canDiscuss,
      useVideoPreviewCourseBanner: courseDetailData?.useVideoPreviewCourseBanner,
      videoPreviewUrl: courseDetailData?.videoPreviewUrl,
      videoPreviewSubtitles: courseDetailData?.videoPreviewSubtitles,
      enrollmentStatus: courseEnrollmentData?.enrollmentStatus,
      university: courseDetailSeoData?.university,
      universityLogoUrl: courseDetailData?.universityLogoUrl,
      degrees: courseDetailSeoData?.degrees,
      examExpiredAt: courseEnrollmentData?.examExpiredAt,
      bannerPrimaryColor: courseDetailData?.bannerPrimaryColor,
      bannerSecondaryColor: courseDetailData?.bannerSecondaryColor,
      degreeCourseGroups: courseDetailData?.degreeCourseGroups,
    };
  }, [courseDetailData, courseDetailSeoData, courseEnrollmentData]);

  return {
    id,
    idName,
    name,
    productLearningTypeId,
    description,
    seoDescription,
    commercialMessage,
    seoEnabled,
    gtmContentId,
    instructors,
    highlightImage2xUrl,
    highlightImage1xUrl,
    hasCertificate,
    hasCertificateBundle,
    criteriaCertificate,
    price,
    videoCount,
    totalVideoDuration,
    countdownDay,
    attachmentFileCount,
    quizCount,
    assignmentCount,
    examinationCount,
    articleCount,
    review,
    isCourseDetailSeoLoading,
    isCourseDetailLoading,
    isCourseDetailError,
    isUseMailTemplate,
    mailContent,
    mailSubject,
    tweeterContent,
    handleFetchCourseDetail,
    handleResetCourseDetail,
    canDiscuss,
    useVideoPreviewCourseBanner,
    videoPreviewUrl,
    videoPreviewSubtitles,
    enrollmentStatus,
    university,
    universityLogoUrl,
    degrees,
    examExpiredAt,
    courseDetailData,
    bannerPrimaryColor,
    bannerSecondaryColor,
    degreeCourseGroups,
  };
};

export default useCourseDetail;
